/**
 * Ethics & Grievance Reporting — confidential workplace concern channel.
 *
 * Replaces the old "My Reports" module. Provides:
 *   • A formal, policy-aligned intake form (with anonymous option)
 *   • A list view that respects the same backend RBAC (staff only see own
 *     non-anonymous cases; reviewers see all)
 *   • A case-detail dialog with thread comments, audit drawer, and a
 *     reviewer action panel exposing every workflow transition.
 *
 * Backend: /api/ethics-cases/*
 */
import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, formatDate, formatDateTime } from "@/lib/api";
import { useAuth, hasMinRole } from "@/contexts/AuthContext";
import { PageHeader, StatusBadge } from "@/components/app/ui-bits";
import AuditTrailDrawer from "@/components/app/AuditTrailDrawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
  DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ShieldCheck, Lock, UserCircle, Eye, Plus, ChatCircle, ArrowRight,
  Scales, ListChecks, Warning,
} from "@phosphor-icons/react";
import { toast } from "sonner";

// ── Vocab → friendly labels ─────────────────────────────────────────
const CATEGORY_LABELS = {
  grievance: "Grievance",
  psea: "PSEA · Protection from Sexual Exploitation & Abuse",
  sexual_harassment: "Sexual Harassment",
  abuse: "Abuse",
  bullying: "Bullying",
  discrimination: "Discrimination",
  misconduct: "Misconduct",
  retaliation: "Retaliation",
  conflict_of_interest: "Conflict of Interest",
  code_of_conduct_breach: "Code of Conduct Breach",
  ethics_complaint: "Ethics Complaint",
  workplace_safety: "Workplace Safety Concern",
  other: "Other Serious Concern",
};
const STATUS_LABELS = {
  submitted: "Submitted",
  under_review: "Under review",
  escalated: "Escalated",
  investigation_ongoing: "Investigation ongoing",
  action_required: "Action required",
  resolved: "Resolved",
  closed: "Closed",
  returned: "Returned to reporter",
};

// ── Severity & status meta ──────────────────────────────────────────
const SEVERITY_META = {
  critical: { label: "Critical", tone: "border-rose-500 bg-rose-50 text-rose-700" },
  high:     { label: "High",     tone: "border-amber-500 bg-amber-50 text-amber-700" },
  medium:   { label: "Medium",   tone: "border-blue-500 bg-blue-50 text-blue-700" },
  low:      { label: "Low",      tone: "border-slate-300 bg-slate-50 text-slate-700" },
  unspecified: { label: "—",      tone: "border-slate-200 bg-white text-slate-500" },
};
const STATUS_TONE = {
  submitted:             "border-blue-200 bg-blue-50 text-blue-700",
  under_review:          "border-indigo-200 bg-indigo-50 text-indigo-700",
  escalated:             "border-amber-300 bg-amber-50 text-amber-800",
  investigation_ongoing: "border-violet-300 bg-violet-50 text-violet-800",
  action_required:       "border-orange-300 bg-orange-50 text-orange-800",
  resolved:              "border-emerald-300 bg-emerald-50 text-emerald-700",
  closed:                "border-slate-300 bg-slate-100 text-slate-700",
  returned:              "border-slate-200 bg-slate-50 text-slate-600",
};

// ── New-case dialog ─────────────────────────────────────────────────
function NewCaseDialog({ meta, onCreated }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const empty = {
    title: "", category: "", issue_type: "",
    anonymous: false, incident_date: "", incident_location: "",
    people_involved: "", description: "", witnesses: "",
    preferred_contact: "email", preferred_contact_value: "",
    confidentiality_level: "high", severity: "medium",
    assigned_reviewer_email: "",
  };
  const [form, setForm] = useState(empty);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async () => {
    if (!form.title || form.title.length < 4) return toast.error("Provide a clear case title (min 4 chars).");
    if (!form.category) return toast.error("Choose a category.");
    if (!form.description || form.description.length < 10) return toast.error("Describe the incident in at least 10 characters.");
    setBusy(true);
    try {
      const data = await api.post("/ethics-cases", form);
      toast.success(`Case ${data.data.case_no} submitted.`);
      onCreated?.(data.data);
      setOpen(false); setForm(empty);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="btn-primary-blue" data-testid="ethics-new-btn">
          <Plus size={14} className="mr-2" /> Submit a concern
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Lock size={18} className="text-slate-700" weight="duotone" />
            <DialogTitle className="font-display">Submit a confidential concern</DialogTitle>
          </div>
          <DialogDescription>
            All cases are routed to authorised ethics & grievance reviewers only. You may submit
            anonymously — once you do, your identity is not stored or retrievable for that case.
          </DialogDescription>
        </DialogHeader>

        {/* Anonymous toggle */}
        <div className="flex items-start gap-3 rounded border border-amber-200 bg-amber-50 p-3">
          <input
            id="anon-toggle"
            type="checkbox"
            className="mt-0.5"
            checked={form.anonymous}
            onChange={(e) => set("anonymous", e.target.checked)}
            data-testid="ethics-anonymous-toggle"
          />
          <label htmlFor="anon-toggle" className="text-xs leading-snug">
            <span className="font-semibold text-amber-900">Submit anonymously.</span>{" "}
            <span className="text-amber-800">
              If checked, your name, email and contact details will not be stored on this case.
              You will not be able to view or track this case yourself afterwards.
            </span>
          </label>
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label>Case title <span className="text-red-600">*</span></Label>
            <Input value={form.title} onChange={(e) => set("title", e.target.value)}
              placeholder="Short, factual title (avoid names)" data-testid="ethics-title" />
          </div>
          <div>
            <Label>Category <span className="text-red-600">*</span></Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="ethics-category"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent>
                {(meta?.categories || []).map((c) => (
                  <SelectItem key={c} value={c}>{CATEGORY_LABELS[c] || c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Specific issue type</Label>
            <Input value={form.issue_type} onChange={(e) => set("issue_type", e.target.value)}
              placeholder="e.g. verbal abuse, unfair targeting…" data-testid="ethics-issue-type" />
          </div>
          <div>
            <Label>Incident date</Label>
            <Input type="date" value={form.incident_date}
              onChange={(e) => set("incident_date", e.target.value)} />
          </div>
          <div>
            <Label>Incident location</Label>
            <Input value={form.incident_location}
              onChange={(e) => set("incident_location", e.target.value)}
              placeholder="Office, site, project, channel…" />
          </div>
          <div className="sm:col-span-2">
            <Label>People involved (optional)</Label>
            <Input value={form.people_involved} onChange={(e) => set("people_involved", e.target.value)}
              placeholder="Use initials or roles if you prefer not to name." />
          </div>
          <div className="sm:col-span-2">
            <Label>Detailed description <span className="text-red-600">*</span></Label>
            <Textarea rows={6} value={form.description}
              onChange={(e) => set("description", e.target.value)}
              placeholder="Describe what happened, when, who was involved, and the impact."
              data-testid="ethics-description" />
            <div className="mt-1 text-[10px] text-slate-500">
              Minimum 10 characters · {form.description.length}/8000
            </div>
          </div>
          <div className="sm:col-span-2">
            <Label>Witnesses</Label>
            <Input value={form.witnesses} onChange={(e) => set("witnesses", e.target.value)}
              placeholder="Names or roles of any witnesses (optional)" />
          </div>
          <div>
            <Label>Confidentiality level</Label>
            <Select value={form.confidentiality_level}
              onValueChange={(v) => set("confidentiality_level", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="standard">Standard</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="restricted">Restricted (named reviewer only)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Preferred contact method</Label>
            <Select value={form.preferred_contact}
              onValueChange={(v) => set("preferred_contact", v)}
              disabled={form.anonymous}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="phone">Phone</SelectItem>
                <SelectItem value="in_person">In person</SelectItem>
                <SelectItem value="none">No follow-up needed</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {!form.anonymous && form.preferred_contact !== "none" && (
            <div className="sm:col-span-2">
              <Label>Contact value</Label>
              <Input value={form.preferred_contact_value}
                onChange={(e) => set("preferred_contact_value", e.target.value)}
                placeholder={form.preferred_contact === "phone" ? "+971…" : "you@descon.group"} />
            </div>
          )}
          <div className="sm:col-span-2">
            <Label>Nominate a reviewer (optional)</Label>
            <Input value={form.assigned_reviewer_email}
              onChange={(e) => set("assigned_reviewer_email", e.target.value)}
              placeholder="Leave blank to route to the default ethics queue." />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy} className="btn-primary-blue" data-testid="ethics-submit">
            <ShieldCheck size={14} className="mr-2" /> {busy ? "Submitting…" : "Submit confidentially"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── Case-detail dialog ──────────────────────────────────────────────
function CaseDetail({ caseId, isReviewer, onClose, refresh }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newStatus, setNewStatus] = useState("under_review");
  const [comment, setComment] = useState("");
  const [actionTaken, setActionTaken] = useState("");
  const [resolution, setResolution] = useState("");
  const [thread, setThread] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/ethics-cases/${caseId}`);
      setData(d);
      setActionTaken(d.action_taken || "");
      setResolution(d.resolution_summary || "");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (caseId) load(); /* eslint-disable-next-line */ }, [caseId]);

  const review = async () => {
    setBusy(true);
    try {
      await api.post(`/ethics-cases/${caseId}/review`, {
        new_status: newStatus, comment, action_taken: actionTaken,
        resolution_summary: resolution,
      });
      toast.success(`Case updated to ${STATUS_LABELS[newStatus] || newStatus}`);
      setComment(""); await load(); refresh?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const postThread = async () => {
    if (!thread.trim()) return;
    setBusy(true);
    try {
      await api.post(`/ethics-cases/${caseId}/comment`, { comment: thread });
      setThread(""); await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!caseId) return null;
  return (
    <Dialog open={!!caseId} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex flex-wrap items-center gap-2">
            <Lock size={16} className="text-slate-700" weight="duotone" />
            <DialogTitle className="font-display">
              {loading ? "Loading…" : data?.case_no || "Case"}
            </DialogTitle>
            {data && <StatusBadge status={data.status} />}
            {data?.anonymous && (
              <span className="rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-[10px] font-mono-alt uppercase tracking-widest text-slate-700">
                <UserCircle size={10} className="inline mr-1" /> Anonymous
              </span>
            )}
          </div>
          <DialogDescription>
            {data?.title || ""}
          </DialogDescription>
        </DialogHeader>

        {!data ? (
          <div className="py-10 text-center font-mono-alt text-xs uppercase tracking-widest text-slate-500">Loading…</div>
        ) : (
          <div className="space-y-4">
            <div className="flex justify-end">
              <AuditTrailDrawer entity="ethics_case" entityId={data.id} label={`Audit · ${data.case_no}`} />
            </div>

            {/* Core fields */}
            <div className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
              <Field k="Category" v={CATEGORY_LABELS[data.category] || data.category} />
              <Field k="Issue type" v={data.issue_type} />
              <Field k="Incident date" v={data.incident_date ? formatDate(data.incident_date) : "—"} />
              <Field k="Incident location" v={data.incident_location} />
              <Field k="People involved" v={data.people_involved} />
              <Field k="Witnesses" v={data.witnesses} />
              <Field k="Confidentiality" v={data.confidentiality_level} />
              <Field k="Preferred contact"
                v={data.anonymous ? "—" : `${data.preferred_contact || "—"}${data.preferred_contact_value ? ` · ${data.preferred_contact_value}` : ""}`} />
              <Field k="Submitted" v={formatDateTime(data.submitted_at)} />
              <Field k="By" v={data.anonymous ? "Anonymous" : (data.submitted_by_name || data.submitted_by_email || "—")} />
              <Field k="Assigned reviewer" v={data.assigned_reviewer_email} />
              <Field k="Closed" v={data.closed_at ? `${formatDateTime(data.closed_at)} · ${data.closed_by_email || "—"}` : "—"} />
              <div className="sm:col-span-2">
                <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">Description</div>
                <div className="mt-0.5 whitespace-pre-wrap rounded bg-slate-50 p-3 text-sm text-slate-900">
                  {data.description || "—"}
                </div>
              </div>
              {data.action_taken && (
                <div className="sm:col-span-2">
                  <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">Action taken</div>
                  <div className="mt-0.5 whitespace-pre-wrap rounded bg-emerald-50/50 border border-emerald-200 p-3 text-sm">{data.action_taken}</div>
                </div>
              )}
              {data.resolution_summary && (
                <div className="sm:col-span-2">
                  <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">Resolution summary</div>
                  <div className="mt-0.5 whitespace-pre-wrap rounded bg-blue-50 border border-blue-200 p-3 text-sm">{data.resolution_summary}</div>
                </div>
              )}
            </div>

            {/* Reviewer-only action panel */}
            {isReviewer && data.status !== "closed" && (
              <div className="rounded-lg border border-slate-300 bg-slate-50 p-4">
                <div className="mb-2 flex items-center gap-2 font-display text-sm font-semibold text-slate-900">
                  <ListChecks size={14} /> Reviewer action
                </div>
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <Label>New status</Label>
                    <Select value={newStatus} onValueChange={setNewStatus}>
                      <SelectTrigger data-testid="ethics-action-status"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(STATUS_LABELS).filter(([k]) => k !== "submitted").map(([k, l]) => (
                          <SelectItem key={k} value={k}>{l}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Reviewer comment</Label>
                    <Input value={comment} onChange={(e) => setComment(e.target.value)}
                      placeholder="Internal note attached to this action" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Action taken</Label>
                    <Textarea rows={2} value={actionTaken} onChange={(e) => setActionTaken(e.target.value)} />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Resolution summary <span className="text-[10px] text-slate-400">(required on close / resolve)</span></Label>
                    <Textarea rows={2} value={resolution} onChange={(e) => setResolution(e.target.value)} />
                  </div>
                </div>
                <div className="mt-3 flex justify-end">
                  <Button onClick={review} disabled={busy} className="btn-primary-blue" data-testid="ethics-action-submit">
                    Apply action <ArrowRight size={14} className="ml-1.5" />
                  </Button>
                </div>
              </div>
            )}

            {/* Thread comments */}
            <div className="rounded-lg border border-slate-200 bg-white p-4">
              <div className="mb-2 flex items-center gap-2 font-display text-sm font-semibold text-slate-900">
                <ChatCircle size={14} /> Case thread ({(data.comments || []).length})
              </div>
              <ul className="space-y-2">
                {(data.comments || []).length === 0 && (
                  <li className="text-xs text-slate-500 italic">No comments yet.</li>
                )}
                {(data.comments || []).map((c) => (
                  <li key={c.id} className="rounded border border-slate-100 bg-slate-50 p-2.5 text-sm">
                    <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">
                      {c.by_name || c.by_email} · {(c.by_role || "").replace(/_/g, " ")}
                      {c.linked_status ? ` · marked ${STATUS_LABELS[c.linked_status] || c.linked_status}` : ""}
                      {" · "}{formatDateTime(c.at)}
                    </div>
                    <div className="mt-1 whitespace-pre-wrap text-slate-800">{c.text}</div>
                  </li>
                ))}
              </ul>
              {/* Reporter & reviewer follow-ups */}
              <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                <Input value={thread} onChange={(e) => setThread(e.target.value)}
                  placeholder="Add a follow-up message…" data-testid="ethics-thread-input" />
                <Button onClick={postThread} disabled={busy || !thread.trim()} variant="outline" data-testid="ethics-thread-post">
                  Post
                </Button>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ k, v }) {
  return (
    <div className="border-b border-slate-100 pb-1.5">
      <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">{k}</div>
      <div className="mt-0.5 text-sm text-slate-900">{v || "—"}</div>
    </div>
  );
}

// ── Main page ───────────────────────────────────────────────────────
export default function EthicsReporting() {
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusF, setStatusF] = useState("all");
  const [categoryF, setCategoryF] = useState("all");
  const [severityF, setSeverityF] = useState("all");
  const [reviewerF, setReviewerF] = useState("all");
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [stats, setStats] = useState(null);
  const [reviewers, setReviewers] = useState([]);

  const isReviewer = meta?.is_reviewer
    || hasMinRole(user, "admin")
    || (user?.role === "manager" && (user?.dept_scope === "hr" || !user?.dept_scope));

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusF !== "all") params.set("status", statusF);
      if (categoryF !== "all") params.set("category", categoryF);
      if (severityF !== "all") params.set("severity", severityF);
      if (reviewerF !== "all") params.set("assigned_reviewer_email", reviewerF);
      if (search) params.set("search", search);
      const [m, l] = await Promise.all([
        api.get("/ethics-cases/meta"),
        api.get(`/ethics-cases?${params.toString()}`),
      ]);
      setMeta(m.data);
      setItems(l.data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [statusF, categoryF, severityF, reviewerF, search]);

  // Load stats + reviewers list only for authorised reviewers
  useEffect(() => {
    if (!isReviewer) { setStats(null); return; }
    (async () => {
      try {
        const { data } = await api.get("/ethics-cases/stats/summary");
        setStats(data);
      } catch (e) { /* silent — stats are optional */ }
      try {
        const { data } = await api.get("/users");
        // List of potential reviewers: anyone with manager+ rank
        const r = (data || []).filter((u) =>
          ["super_admin", "ceo", "admin", "management", "hr_manager", "hr_officer", "manager"].includes(u.role)
        );
        setReviewers(r);
      } catch (e) { /* silent */ }
    })();
  }, [isReviewer]);

  const counts = useMemo(() => {
    const c = {};
    items.forEach((i) => { c[i.status] = (c[i.status] || 0) + 1; });
    return c;
  }, [items]);

  // ── Reviewer mode dashboard ─────────────────────────────────────────
  if (isReviewer) {
    return (
      <ReviewerDashboard
        user={user} meta={meta} items={items} loading={loading} counts={counts}
        stats={stats} reviewers={reviewers}
        statusF={statusF} setStatusF={setStatusF}
        categoryF={categoryF} setCategoryF={setCategoryF}
        severityF={severityF} setSeverityF={setSeverityF}
        reviewerF={reviewerF} setReviewerF={setReviewerF}
        search={search} setSearch={setSearch}
        setSelectedId={setSelectedId}
        selectedId={selectedId}
        refresh={load}
      />
    );
  }

  // ── Employee mode (original simple submit + track view) ─────────────
  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Confidential · policy-aligned"
        title="Ethics & Grievance Reporting"
        description="A protected channel for raising grievances, ethics concerns, PSEA matters, harassment, discrimination, and other serious workplace issues. Cases are visible only to authorised reviewers and (for non-anonymous cases) to the reporter themselves."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <NewCaseDialog meta={meta} onCreated={load} />
          </div>
        }
      />

      {/* Policy banner */}
      <div className="dc-card p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <Scales size={22} className="text-slate-700 shrink-0 mt-0.5" weight="duotone" />
          <div className="text-sm text-slate-700">
            <div className="font-display font-semibold text-slate-900">Confidential by policy</div>
            <p className="mt-0.5 text-xs leading-relaxed text-slate-600">
              Reports submitted here are routed only to the appropriate internal authority —
              HR, Management, the Ethics Focal Point or the Safeguarding Officer. Retaliation
              against any person who reports in good faith is strictly prohibited under
              Descon's Code of Conduct and Whistleblower Protection Policy. You may submit
              anonymously; once you do, your identity is not stored.
            </p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[180px]">
          <Label className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">Status</Label>
          <Select value={statusF} onValueChange={setStatusF}>
            <SelectTrigger data-testid="ethics-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All ({items.length})</SelectItem>
              {(meta?.statuses || []).map((s) => (
                <SelectItem key={s} value={s}>
                  {STATUS_LABELS[s] || s}{counts[s] ? ` (${counts[s]})` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1 min-w-[180px]">
          <Label className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">Category</Label>
          <Select value={categoryF} onValueChange={setCategoryF}>
            <SelectTrigger data-testid="ethics-filter-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {(meta?.categories || []).map((c) => (
                <SelectItem key={c} value={c}>{CATEGORY_LABELS[c] || c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* List */}
      <div className="dc-card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center font-mono-alt text-xs uppercase tracking-widest text-slate-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center">
            <Warning size={28} className="mx-auto text-slate-400" />
            <div className="mt-2 font-display text-sm font-semibold text-slate-900">No cases yet</div>
            <div className="mt-1 text-xs text-slate-500">
              {isReviewer ? "There are no ethics cases in this filter view." : "You haven't submitted any concerns yet. Use the button above to raise one — anonymous submission is supported."}
            </div>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <table className="hidden w-full md:table">
              <thead className="bg-slate-50 text-left text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-5 py-3">Case</th>
                  <th className="px-5 py-3">Category</th>
                  <th className="px-5 py-3">Title</th>
                  <th className="px-5 py-3">Reporter</th>
                  <th className="px-5 py-3">Submitted</th>
                  <th className="px-5 py-3">Status</th>
                  <th className="px-5 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((c) => (
                  <tr key={c.id} className="text-sm cursor-pointer hover:bg-slate-50 transition"
                      onClick={() => setSelectedId(c.id)} data-testid={`ethics-row-${c.id}`}>
                    <td className="px-5 py-3 font-mono-alt text-xs font-bold text-slate-900">{c.case_no}</td>
                    <td className="px-5 py-3 text-xs">
                      <span className="rounded bg-slate-100 px-2 py-0.5 font-mono-alt uppercase tracking-wider text-slate-700">
                        {CATEGORY_LABELS[c.category] || c.category}
                      </span>
                    </td>
                    <td className="px-5 py-3 font-semibold text-slate-900 truncate max-w-xs">{c.title}</td>
                    <td className="px-5 py-3 text-xs">
                      {c.anonymous
                        ? <span className="text-slate-500 italic">Anonymous</span>
                        : (c.submitted_by_name || c.submitted_by_email || "—")}
                    </td>
                    <td className="px-5 py-3 font-mono-alt text-xs">{formatDate(c.submitted_at)}</td>
                    <td className="px-5 py-3"><StatusBadge status={c.status} /></td>
                    <td className="px-5 py-3 text-right">
                      <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setSelectedId(c.id); }}
                        data-testid={`ethics-open-${c.id}`}>
                        <Eye size={12} className="mr-1" /> Open
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Mobile cards */}
            <ul className="divide-y divide-slate-100 md:hidden">
              {items.map((c) => (
                <li key={c.id} className="p-4 cursor-pointer hover:bg-slate-50 transition"
                    onClick={() => setSelectedId(c.id)} data-testid={`ethics-mrow-${c.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-mono-alt text-xs font-bold text-slate-900">{c.case_no}</div>
                      <div className="mt-0.5 text-sm font-semibold truncate">{c.title}</div>
                      <div className="mt-0.5 text-[11px] text-slate-500">
                        {CATEGORY_LABELS[c.category] || c.category}
                      </div>
                    </div>
                    <StatusBadge status={c.status} />
                  </div>
                  <div className="mt-2 text-[11px] text-slate-500">
                    {c.anonymous ? "Anonymous" : (c.submitted_by_name || c.submitted_by_email)} · {formatDate(c.submitted_at)}
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      <CaseDetail caseId={selectedId} isReviewer={isReviewer}
        onClose={() => setSelectedId(null)} refresh={load} />
    </div>
  );
}

// ── Reviewer Dashboard mode ─────────────────────────────────────────
function ReviewerDashboard({
  user, meta, items, loading, counts, stats, reviewers,
  statusF, setStatusF, categoryF, setCategoryF,
  severityF, setSeverityF, reviewerF, setReviewerF,
  search, setSearch, selectedId, setSelectedId, refresh,
}) {
  const s = stats || {};
  return (
    <div className="space-y-6" data-testid="ethics-reviewer-dashboard">
      <PageHeader
        eyebrow="Reviewer mode · case management"
        title="Ethics & Grievance Reviewer Dashboard"
        description="Authorised reviewers only. Triage, assign, escalate, investigate, resolve and close cases. Anonymous cases never reveal the reporter — even to you."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <NewCaseDialog meta={meta} onCreated={refresh} />
          </div>
        }
      />

      {/* KPI tiles */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6" data-testid="ethics-kpi-row">
        <KpiTile testid="kpi-total"          icon={Scales}       label="Total cases"      value={s.total ?? 0} tone="slate" />
        <KpiTile testid="kpi-pending"        icon={ListChecks}   label="Pending review"   value={s.pending_review ?? 0} tone={(s.pending_review||0) > 0 ? "amber" : "slate"} />
        <KpiTile testid="kpi-investigation"  icon={Warning}      label="Investigation"    value={s.investigation_ongoing ?? 0} tone="violet" />
        <KpiTile testid="kpi-escalated"      icon={ArrowRight}   label="Escalated"        value={s.escalated ?? 0} tone={(s.escalated||0) > 0 ? "amber" : "slate"} />
        <KpiTile testid="kpi-overdue"        icon={Warning}      label="Overdue (>14d)"   value={s.overdue ?? 0} tone={(s.overdue||0) > 0 ? "red" : "slate"} />
        <KpiTile testid="kpi-resolved"       icon={ShieldCheck}  label="Resolved · closed" value={(s.resolved || 0) + (s.closed || 0)} tone="emerald" />
      </div>

      {/* Secondary KPI row — supporting metrics */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <KpiTile testid="kpi-new"          label="New / submitted"   value={s.new_submitted ?? 0} tone="blue"    />
        <KpiTile testid="kpi-under-review" label="Under review"      value={s.under_review ?? 0}  tone="indigo"  />
        <KpiTile testid="kpi-action"       label="Action required"   value={s.action_required ?? 0} tone={(s.action_required||0) > 0 ? "amber" : "slate"} />
        <KpiTile testid="kpi-anonymous"    label="Anonymous cases"   value={s.anonymous ?? 0} tone="slate" />
        <KpiTile testid="kpi-high-risk"    label="High-risk open"    value={s.high_risk_open ?? 0} tone={(s.high_risk_open||0) > 0 ? "red" : "slate"} />
        <KpiTile testid="kpi-resolution"   label="Avg resolution"    value={`${s.avg_resolution_days ?? 0} d`} tone="slate" />
      </div>

      {/* By category + by reviewer */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <DistributionCard
          title="Cases by category"
          rows={Object.entries(s.by_category || {}).sort((a, b) => b[1] - a[1])}
          labeller={(k) => CATEGORY_LABELS[k] || k}
          onClick={(k) => setCategoryF(k)}
          testid="dist-category"
        />
        <DistributionCard
          title="Open cases by reviewer"
          rows={Object.entries(s.by_reviewer || {}).sort((a, b) => b[1] - a[1])}
          labeller={(k) => k === "unassigned" ? "— Unassigned" : k}
          onClick={(k) => setReviewerF(k)}
          testid="dist-reviewer"
        />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">Search</Label>
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Case number, title, or category…" data-testid="ethics-search" />
        </div>
        <div className="min-w-[160px]">
          <Label className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">Status</Label>
          <Select value={statusF} onValueChange={setStatusF}>
            <SelectTrigger data-testid="ethics-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All ({items.length})</SelectItem>
              {(meta?.statuses || []).map((st) => (
                <SelectItem key={st} value={st}>
                  {STATUS_LABELS[st] || st}{counts[st] ? ` (${counts[st]})` : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[170px]">
          <Label className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">Category</Label>
          <Select value={categoryF} onValueChange={setCategoryF}>
            <SelectTrigger data-testid="ethics-filter-category"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {(meta?.categories || []).map((c) => (
                <SelectItem key={c} value={c}>{CATEGORY_LABELS[c] || c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[140px]">
          <Label className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">Severity</Label>
          <Select value={severityF} onValueChange={setSeverityF}>
            <SelectTrigger data-testid="ethics-filter-severity"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All severities</SelectItem>
              {(meta?.severities || ["low","medium","high","critical"]).map((sv) => (
                <SelectItem key={sv} value={sv}>{SEVERITY_META[sv]?.label || sv}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[200px]">
          <Label className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">Reviewer</Label>
          <Select value={reviewerF} onValueChange={setReviewerF}>
            <SelectTrigger data-testid="ethics-filter-reviewer"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All reviewers</SelectItem>
              <SelectItem value="unassigned">— Unassigned</SelectItem>
              {reviewers.map((r) => (
                <SelectItem key={r.email} value={r.email}>{r.name || r.email}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {(statusF !== "all" || categoryF !== "all" || severityF !== "all" || reviewerF !== "all" || search) && (
          <Button variant="outline" size="sm"
            onClick={() => { setStatusF("all"); setCategoryF("all"); setSeverityF("all"); setReviewerF("all"); setSearch(""); }}
            data-testid="ethics-clear-filters">
            Clear
          </Button>
        )}
      </div>

      {/* Cases table */}
      <div className="dc-card overflow-hidden">
        {loading ? (
          <div className="p-8 text-center font-mono-alt text-xs uppercase tracking-widest text-slate-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center" data-testid="ethics-empty">
            <Warning size={28} className="mx-auto text-slate-400" />
            <div className="mt-2 font-display text-sm font-semibold text-slate-900">No cases in this view</div>
            <div className="mt-1 text-xs text-slate-500">Adjust the filters above to widen your selection.</div>
          </div>
        ) : (
          <>
          {/* Desktop / tablet table — horizontal scroll fallback prevents page overflow */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full min-w-[820px]" data-testid="ethics-reviewer-table">
              <thead className="bg-slate-50 text-left text-[10px] font-mono-alt uppercase tracking-widest text-slate-500">
                <tr>
                  <th className="px-3 py-3 w-[88px]">Case</th>
                  <th className="px-3 py-3">Title / Category</th>
                  <th className="px-3 py-3 w-[96px]">Severity</th>
                  <th className="px-3 py-3 w-[140px]">Reporter</th>
                  <th className="px-3 py-3 w-[140px]">Reviewer</th>
                  <th className="px-3 py-3 w-[90px] whitespace-nowrap">Submitted</th>
                  <th className="px-3 py-3 w-[120px]">Status</th>
                  <th className="px-3 py-3 w-[90px] text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((c) => {
                  const sev = SEVERITY_META[c.severity || "unspecified"] || SEVERITY_META.unspecified;
                  return (
                    <tr key={c.id} className="text-sm cursor-pointer hover:bg-slate-50 transition"
                        onClick={() => setSelectedId(c.id)} data-testid={`ethics-rrow-${c.id}`}>
                      <td className="px-3 py-3 font-mono-alt text-[11px] font-bold text-slate-900 whitespace-nowrap align-top">{c.case_no}</td>
                      <td className="px-3 py-3 align-top">
                        <div className="font-semibold text-slate-900 break-words">{c.title}</div>
                        <div className="mt-0.5">
                          <span className="rounded bg-slate-100 px-2 py-0.5 font-mono-alt uppercase tracking-wider text-[10px] text-slate-700 inline-block break-words">
                            {CATEGORY_LABELS[c.category] || c.category}
                          </span>
                        </div>
                      </td>
                      <td className="px-3 py-3 align-top">
                        <span className={`inline-flex items-center rounded border px-2 py-0.5 text-[10px] font-medium ${sev.tone}`}>
                          {sev.label}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-xs align-top break-words">
                        {c.anonymous
                          ? <span className="text-slate-500 italic flex items-center gap-1"><Lock size={10} /> Anonymous</span>
                          : (c.submitted_by_name || c.submitted_by_email || "—")}
                      </td>
                      <td className="px-3 py-3 text-xs align-top break-words">
                        {c.assigned_reviewer_email || <span className="text-slate-400 italic">Unassigned</span>}
                      </td>
                      <td className="px-3 py-3 font-mono-alt text-xs whitespace-nowrap align-top">{formatDate(c.submitted_at)}</td>
                      <td className="px-3 py-3 align-top">
                        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium ${STATUS_TONE[c.status] || "border-slate-200 text-slate-700"}`}>
                          {STATUS_LABELS[c.status] || c.status}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-right align-top">
                        <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setSelectedId(c.id); }}
                          data-testid={`ethics-ropen-${c.id}`}>
                          <Eye size={12} className="mr-1" /> Review
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Mobile stacked cards */}
          <ul className="divide-y divide-slate-100 md:hidden">
            {items.map((c) => {
              const sev = SEVERITY_META[c.severity || "unspecified"] || SEVERITY_META.unspecified;
              return (
                <li key={c.id}
                    className="p-4 cursor-pointer hover:bg-slate-50 transition active:bg-slate-100"
                    onClick={() => setSelectedId(c.id)}
                    data-testid={`ethics-rmrow-${c.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono-alt text-[11px] font-bold text-slate-900">{c.case_no}</span>
                        <span className={`inline-flex items-center rounded border px-1.5 py-0.5 text-[9px] font-medium ${sev.tone}`}>
                          {sev.label}
                        </span>
                      </div>
                      <div className="mt-1 text-sm font-semibold text-slate-900 break-words">{c.title}</div>
                      <div className="mt-1 text-[11px] text-slate-500 break-words">
                        {CATEGORY_LABELS[c.category] || c.category}
                      </div>
                    </div>
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium whitespace-nowrap ${STATUS_TONE[c.status] || "border-slate-200 text-slate-700"}`}>
                      {STATUS_LABELS[c.status] || c.status}
                    </span>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px] text-slate-600">
                    <div className="truncate">
                      <span className="text-slate-400">Reporter:</span> {c.anonymous
                        ? <span className="italic">Anonymous</span>
                        : <span className="text-slate-800">{c.submitted_by_name || c.submitted_by_email || "—"}</span>}
                    </div>
                    <div className="truncate">
                      <span className="text-slate-400">Submitted:</span> <span className="font-mono-alt">{formatDate(c.submitted_at)}</span>
                    </div>
                    <div className="truncate col-span-2">
                      <span className="text-slate-400">Reviewer:</span> {c.assigned_reviewer_email || <span className="italic text-slate-500">Unassigned</span>}
                    </div>
                  </div>
                  <div className="mt-3 flex justify-end">
                    <Button size="sm" variant="outline"
                      onClick={(e) => { e.stopPropagation(); setSelectedId(c.id); }}
                      data-testid={`ethics-rmopen-${c.id}`}
                      className="w-full">
                      <Eye size={12} className="mr-1" /> Open case
                    </Button>
                  </div>
                </li>
              );
            })}
          </ul>
          </>
        )}
      </div>

      <CaseDetail caseId={selectedId} isReviewer={true}
        onClose={() => setSelectedId(null)} refresh={refresh} />
    </div>
  );
}

const TONE_BORDER = {
  blue:    "border-l-[#1E5CBA]",
  emerald: "border-l-emerald-500",
  amber:   "border-l-amber-500",
  red:     "border-l-rose-500",
  indigo:  "border-l-indigo-500",
  violet:  "border-l-violet-500",
  slate:   "border-l-slate-300",
};
const TONE_TEXT = {
  blue: "text-[#1E5CBA]", emerald: "text-emerald-700", amber: "text-amber-700",
  red: "text-rose-700", indigo: "text-indigo-700", violet: "text-violet-700",
  slate: "text-slate-700",
};

function KpiTile({ testid, icon: Icon, label, value, tone = "slate" }) {
  return (
    <div className={`dc-card p-3 border-l-4 ${TONE_BORDER[tone]}`} data-testid={testid}>
      <div className="flex items-center gap-1.5">
        {Icon && <Icon size={13} className="text-slate-400" />}
        <div className="text-[10px] font-mono-alt uppercase tracking-widest text-slate-500 truncate">{label}</div>
      </div>
      <div className={`mt-1 font-display text-2xl font-semibold tabular-nums ${TONE_TEXT[tone]}`}>{value}</div>
    </div>
  );
}

function DistributionCard({ title, rows, labeller, onClick, testid }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="dc-card p-4" data-testid={testid}>
        <div className="font-display text-sm font-semibold text-slate-900">{title}</div>
        <div className="mt-3 text-xs text-slate-400">No data yet.</div>
      </div>
    );
  }
  const max = Math.max(...rows.map((r) => r[1]));
  return (
    <div className="dc-card p-4" data-testid={testid}>
      <div className="font-display text-sm font-semibold text-slate-900 mb-2">{title}</div>
      <ul className="space-y-1.5">
        {rows.slice(0, 8).map(([k, v]) => (
          <li key={k}>
            <button type="button" onClick={() => onClick?.(k)}
              className="w-full text-left rounded px-1.5 py-1 hover:bg-slate-50 transition group">
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-700 group-hover:text-slate-900 truncate">{labeller ? labeller(k) : k}</span>
                <span className="font-mono-alt text-slate-500 tabular-nums">{v}</span>
              </div>
              <div className="mt-0.5 h-1.5 rounded bg-slate-100 overflow-hidden">
                <div className="h-full bg-[#1E5CBA]" style={{ width: `${(v / max) * 100}%` }} />
              </div>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

